import React, { useEffect,useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// Redux actions
import { fetchICTSalesCards } from "../../../../../store/ictSalesCardSlice";
import { fetchIctCostByIctId } from "../../../../../store/ictCostSlice";
import { fetchDCData } from "../../../../../store/dcDataSlice";
import { fetchTrainers } from "../../../../../store/trainersSlice";
import { fetchTrainingCards } from "../../../../../store/trainingCardsSlice";
import { fetchHoursByICTId } from "../../../../../store/hoursSlice";
import { fetchEmployees } from "../../../../../store/empDataSlice";
import { fetchICTData } from "../../../../../store/uiSlice";
import { fetchLearners } from "../../../../../store/learnersSlice";
import { fetchMiscExpenses } from "../../../../../store/miscExpenseDataSlice";
import ICTBillInvoiceInsert from "../billInvoice/ICTBillInvoiceInsert";
import { fetchTourData }        from "../../../../../store/tourDataSlice";
import { fetchTravelData }      from "../../../../../store/travelDataSlice";
import { fetchAccommodations }  from "../../../../../store/accommodationSlice";

import "../../../../../Styles/Common.css";

function ICTSalesCardReceiptPopup() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [showInvoicePopup, setShowInvoicePopup] = useState(false);
  // 1) Pull all data from Redux with useSelector (unconditional hooks)

  // ICTSalesCards
  const {
    data: salesCards = [],
    loading: salesLoading,
    error: salesError,
  } = useSelector((state) => state.ictSalesCards);

  // ICTCosts
  const {
    ictCosts = [],
    loading: ictCostLoading,
    error: ictCostError,
  } = useSelector((state) => state.ictCost);

  // DC data
  const {
    dcData = [],
    loading: dcLoading,
    error: dcError,
  } = useSelector((state) => state.dcData);

  // Trainers
  const {
    trainers = [],
    loading: trainersLoading,
    error: trainersError,
  } = useSelector((state) => state.trainers);

  // TrainingCards
  const {
    trainingCards = [],
    loading: tcLoading,
    error: tcError,
  } = useSelector((state) => state.trainingCards);

  // Hours
  const {
    hoursByIct = {},
    loading: hoursLoading,
    error: hoursError,
  } = useSelector((state) => state.hours);

  // Employees
  const {
    employees = [],
    loading: empLoading,
    error: empError,
  } = useSelector((state) => state.empData);

  // Learners
  const {
    learners = [],
    loading: learnersLoading,
    error: learnersError,
  } = useSelector((state) => state.learners);

  // Also pull all ICT data from uiSlice (where we get ictName)
  const {
    ictData = [],
    loading: ictLoading,
    error: ictError,
  } = useSelector((state) => ({
    ictData: state.ui.ictData,
    loading: state.ui.loading,
    error: state.ui.error,
  }));

  // NEW: miscExpenses for meCost
  const {
    data: miscExpenses = [],
    loading: meLoading,
    error: meError,
  } = useSelector((state) => state.miscExpenseData);
  const {
    tourData = [],
    loading: tourLoading,
    error  : tourError,
  } = useSelector((s) => s.tourData);
 
  const {
    travelData = [],
    loading: travelLoading,
    error  : travelError,
  } = useSelector((s) => s.travelData);
  
  const {
    accommodations = [],
    loading: accLoading,
    error  : accError,
  } = useSelector((s) => s.accommodations);

  // 2) Combined loading or error check
  const isLoading =
  salesLoading || ictCostLoading || dcLoading || trainersLoading ||
  tcLoading  || hoursLoading || empLoading   || ictLoading ||
  learnersLoading || meLoading || tourLoading || travelLoading ||
  accLoading;

  const anyError =
    salesError || ictCostError || dcError || trainersError || tcError ||
    hoursError || empError || ictError || learnersError || meError ||
    tourError  || travelError || accError;

  // 3) Fetch everything needed if not already loaded
  useEffect(() => {
    dispatch(fetchICTSalesCards());
    dispatch(fetchDCData());
    dispatch(fetchTrainers());
    dispatch(fetchTrainingCards());
    dispatch(fetchEmployees());
    dispatch(fetchICTData());  // load all ICT records for ictName
    dispatch(fetchLearners()); // load all learners for name, contact, etc.
    // NEW: also fetch miscExpenses
    dispatch(fetchMiscExpenses());
    dispatch(fetchTourData());
    dispatch(fetchTravelData());
    dispatch(fetchAccommodations());
  }, [dispatch]);

  // 4) Once we know the exact card, fetch cost & hours
  useEffect(() => {
    const scard = salesCards.find((c) => Number(c.id) === Number(id));
    if (scard?.ict_id) {
      dispatch(fetchIctCostByIctId(scard.ict_id));
      dispatch(fetchHoursByICTId(scard.ict_id));
    }
  }, [id, salesCards, dispatch]);

  // 5) If we are still loading, or if there's any error
  if (isLoading) {
    return <p>Loading data...</p>;
  }
  if (anyError) {
    return <p>ERROR: {anyError}</p>;
  }

  // 6) Find the single sales card
  const card = salesCards.find((c) => Number(c.id) === Number(id));
  if (!card) {
    return <p>No matching ICT Sales Card found for ID {id}.</p>;
  }

  // 7) Cost record
  const costRecord = ictCosts.find((c) => Number(c.ictid) === Number(card.ict_id));
  const ictMrp = costRecord?.ictMrp || 0;
  const sellPrice = costRecord?.sellPrice || 0;

  // 8) DC cost
  let dcCost = 0;
  if (card.dcLab_id) {
    const matchedDC = dcData.find((dc) => dc.id === card.dcLab_id);
    if (matchedDC) {
      dcCost = Number(matchedDC.dcCost) || 0;
    }
  }

  const tour   = (tourData ?? [])
    .filter(t => Number(t.tc_id) === Number(card.trainingCard_id))
    .reduce((s, r) => s + Number(r.tourCost ?? 0), 0);
  
  const travel = (travelData ?? [])
    .filter(tr => Number(tr.tc_id) === Number(card.trainingCard_id))
    .reduce((s, r) => s + Number(r.travelCost ?? 0), 0);
  
  const accom  = (accommodations ?? [])
    .filter(a => Number(a.tc_id) === Number(card.trainingCard_id))
    .reduce((s, r) => s + Number(r.accommodationCost ?? 0), 0);

  // NEW: sum up meCost from miscExpenses
  const matchedMiscExpenses = miscExpenses.filter(
    (m) => Number(m.tc_id) === Number(card.trainingCard_id)
  );
  const miscSum = matchedMiscExpenses.reduce((sum, m) => sum + Number(m.meCost || 0), 0);

  // totalAddons includes tour + travel + accom + miscSum
  const totalAddons = tour + travel + accom + miscSum;

  // 10) Find the matching training card to get batch info, location, etc.
  const tCard = trainingCards.find((tc) => Number(tc.id) === Number(card.trainingCard_id));

  // Grab fields from the training card
  const batchStartDate = tCard?.batchStartDate || "";
  const batchEndDate = tCard?.batchEndDate || "";
  const batchStartTime = tCard?.batchStartTime || "";
  const batchEndTime = tCard?.batchEndTime || "";
  const isCustom = tCard?.isCustom || false;
  const isPublic = tCard?.isPublic || false;
  const ictHoursFromTC = tCard?.ictHours || 0;
  const ictLocation = tCard?.ictLocation || "";
  const ictStudyMode = tCard?.ictStudyMode || "";

  // 11) Trainer cost logic
  const getICTDuration = (ict_id) => {
    let total = 0;
    const hoursArr = hoursByIct[ict_id] || [];
    hoursArr.forEach((h) => {
      total += Number(h.TrainingHour || 0);
      total += Number(h.LabHour || 0);
    });
    return total;
  };

  const getBaseTrainerCost = (trainerId, studyMode) => {
    const trainerRec = trainers.find((t) => Number(t.id) === Number(trainerId));
    if (!trainerRec) return 0;

    if (studyMode === "Online") {
      return trainerRec.trainerCostOnline || 0;
    } else if (studyMode === "ClassRoom") {
      return trainerRec.trainerCostClassRoom || 0;
    }
    return 0;
  };

  const getTotalTrainerCost = () => {
    const baseCost = getBaseTrainerCost(card.trainer_id, ictStudyMode);
    const duration = getICTDuration(card.ict_id);
    return baseCost * (duration / 8);
  };

  const totalTrainerCost = getTotalTrainerCost();
  const totalExpenses = totalAddons + dcCost + totalTrainerCost;
  const grossRevenue = sellPrice;
  const netRevenue = grossRevenue - totalExpenses;

  // 12) Identify the ICT name from ui.ictData
  const ictRec = ictData.find((i) => i.id === card.ict_id);
  const ictName = ictRec ? ictRec.ictName : `ICT#${card.ict_id}`;

  // Trainer name
  const trainerRec = trainers.find((t) => Number(t.id) === Number(card.trainer_id));
  let trainerName = "";
  if (trainerRec) {
    const emp = employees.find((e) => Number(e.id) === Number(trainerRec.empId));
    trainerName = emp ? `${emp.empFName} ${emp.empLName}` : `T#${trainerRec.id}`;
  }

  // 13) Fetch **all** learner records that match trainingId === card.trainingCard_id
  const matchedLearners = learners.filter(
    (lrn) => Number(lrn.trainingId) === Number(card.trainingCard_id)
  );
  
  const openInvoicePopup = () => setShowInvoicePopup(true);
  const closeInvoicePopup = () => setShowInvoicePopup(false);

  return (
    <div className="invoice-container">
      <button
        className="print-btn"
        style={{ position:"absolute", top:10, right:10 }}
        onClick={openInvoicePopup}
      >
        <i class="fa fa-edit"></i>
      </button>

      {/* New Invoice Popup */}
      {showInvoicePopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <ICTBillInvoiceInsert
              salesCard={card}
              ictName={ictName}
              batchStartDate={batchStartDate}
              batchStartTime={batchStartTime}
              batchEndDate={batchEndDate}
              batchEndTime={batchEndTime}
              ictLocation={ictLocation}
              ictStudyMode={ictStudyMode}
              duration={ictHoursFromTC}
              trainerName={trainerName}
              tour={tour}
              travel={travel}
              accom={accom}
              miscSum={miscSum}
              dcCost={dcCost}
              totalTrainerCost={totalTrainerCost}
              totalExpenses={totalExpenses}
              ictMrp={ictMrp}
              sellPrice={sellPrice}
              grossRevenue={grossRevenue}
              netRevenue={netRevenue}
              onClose={closeInvoicePopup}
            />
          </div>
        </div>
      )}
      <table>
        <tbody>
          <tr>
            <th colSpan={3}>
              <h2>ICT Sales Card Invoice</h2>
            </th>
          </tr>
          <tr>
            <td>
              <p>Invoice ID: {card.id}</p>
            </td>
            <td colSpan={2}>
              <p>
                Date:{" "}
                {card.created
                  ? new Date(card.created).toLocaleString()
                  : new Date().toLocaleString()}
              </p>
            </td>
          </tr>
          <tr>
            <td><p>ICT Name: {ictName}</p></td>
            <td><p>Trainer: {trainerName || "-"}</p></td>
            <td><p>Training Card ID: {card.trainingCard_id}</p></td>
          </tr>

          {/* Show training-card fields */}
          <tr>
            <td>
              <p>
                Batch Start Date: {batchStartDate}
                <br />
                Batch Start Time: {batchStartTime}
              </p>
            </td>
            <td>
              <p>
                Batch End Date: {batchEndDate}
                <br />
                Batch End Time: {batchEndTime}
              </p>
            </td>
            <td>
              <p>
                {isCustom ? "Customized" : "Standard"}
                <br />
                {isPublic ? "Public" : "Private"}
              </p>
            </td>
          </tr>
          <tr>
            <td><p>Total Duration: {ictHoursFromTC} Hours</p></td>
            <td><p>Location: {ictLocation}</p></td>
            <td><p>Study Mode: {ictStudyMode}</p></td>
          </tr>
          <tr className="amountRow">
            <th colSpan={3}>Learner Details</th>
          </tr>
          {/* Display ALL learners, if any */}
          {matchedLearners.length > 0 ? (
            matchedLearners.map((learner) => (
              <tr key={learner.id}>
                <td>
                  <p>
                    Learner Name: <br />
                    {learner.firstName} {learner.lastName}
                  </p>
                </td>
                <td>
                  <p>
                    Contact: {learner.contact}
                    <br />
                    Email: {learner.email}
                  </p>
                </td>
                <td>
                  <p>
                    Organization: {learner.orgName} <br />
                    Country: {learner.countryName}
                  </p>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={3}>
                <p>No learners found for this training.</p>
              </td>
            </tr>
          )}

          {/* Add spacing row */}
          <tr>
            <td colSpan={3}>
              <br />
            </td>
          </tr>

          <tr className="amountRow">
            <th>Description</th>
            <th colSpan={2}>Amount (₹)</th>
          </tr>
          <tr className="amountRow">
            <td>Tour Bills</td>
            <td colSpan={2}>{tour.toFixed(2)}</td>
          </tr>
          <tr className="amountRow">
            <td>Travel Bills</td>
            <td colSpan={2}>{travel.toFixed(2)}</td>
          </tr>
          <tr className="amountRow">
            <td>Accommodation Bills</td>
            <td colSpan={2}>{accom.toFixed(2)}</td>
          </tr>
          {/* New row for Misc Expense */}
          <tr className="amountRow">
            <td>Misc Expense</td>
            <td colSpan={2}>{miscSum.toFixed(2)}</td>
          </tr>
          <tr className="amountRow">
            <td>DC Cost</td>
            <td colSpan={2}>{dcCost.toFixed(2)}</td>
          </tr>
          <tr className="amountRow">
            <td>Total Trainer Cost</td>
            <td colSpan={2}>{totalTrainerCost.toFixed(2)}</td>
          </tr>
          <tr className="amountRow">
            <th>Total Expenses</th>
            <th colSpan={2}>{totalExpenses.toFixed(2)}</th>
          </tr>
          <tr className="amountRow">
            <td>ICT MRP (Standard For Online)</td>
            <td colSpan={2}>{ictMrp.toFixed(2)}</td>
          </tr>
          <tr className="amountRow">
            <td>ICT Sell Price (Standard For Online)</td>
            <td colSpan={2}>{sellPrice.toFixed(2)}</td>
          </tr>
          {/* <tr className="amountRow">
            <th>Gross Revenue</th>
            <th colSpan={2}>{grossRevenue.toFixed(2)}</th>
          </tr>
          <tr className="amountRow">
            <th>Net Revenue</th>
            <th colSpan={2}>{netRevenue.toFixed(2)}</th>
          </tr> */}
        </tbody>
      </table>
    </div>
  );
}

export default ICTSalesCardReceiptPopup;
